import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { generateSignalInstructions } from '../../api/onboarding';

export interface OnboardingSignalsValidatorProps {
  signalsText: string;
  disabled?: boolean;
  onValidityChange: (isValid: boolean) => void;
  onValidatedInstructions: (
    instructions: string[],
    signalsTrimmed: string
  ) => void;
}

type DisplayState = 'idle' | 'checking' | 'valid' | 'invalid';

const DEBOUNCE_MS = 650;

export const OnboardingSignalsValidator: React.FC<
  OnboardingSignalsValidatorProps
> = ({
  signalsText,
  disabled = false,
  onValidityChange,
  onValidatedInstructions
}) => {
  const [display, setDisplay] = useState<DisplayState>('idle');
  const [invalidMessage, setInvalidMessage] = useState<string | undefined>();
  const [instructionCount, setInstructionCount] = useState<
    number | undefined
  >();

  useEffect(() => {
    if (disabled) {
      return;
    }

    const trimmed = signalsText.trim();
    if (!trimmed) {
      setDisplay('idle');
      setInvalidMessage(undefined);
      setInstructionCount(undefined);
      onValidityChange(false);
      return;
    }

    setDisplay('idle');
    setInvalidMessage(undefined);
    setInstructionCount(undefined);
    onValidityChange(false);

    const ac = new AbortController();
    const timer = window.setTimeout(() => {
      void (async () => {
        setDisplay('checking');
        try {
          const instructions = await generateSignalInstructions(
            trimmed,
            ac.signal
          );
          if (ac.signal.aborted) return;
          onValidatedInstructions(instructions, trimmed);
          setDisplay('valid');
          setInstructionCount(instructions.length);
          onValidityChange(true);
        } catch (e: unknown) {
          if (ac.signal.aborted) return;
          const isAbort = e instanceof Error && e.name === 'AbortError';
          if (isAbort) return;
          setDisplay('invalid');
          const message =
            e instanceof Error ? e.message : 'Something went wrong. Try again.';
          setInvalidMessage(message);
          onValidityChange(false);
        }
      })();
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      ac.abort();
    };
  }, [signalsText, disabled, onValidityChange, onValidatedInstructions]);

  return (
    <Box sx={{ mt: 0.5, mb: 1.5 }} aria-live="polite">
      {display === 'idle' && (
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}
        >
          <Box sx={{ width: 18, height: 18, flexShrink: 0 }} aria-hidden />
          <Typography
            variant="body2"
            component="span"
            sx={{
              color: 'transparent',
              userSelect: 'none',
              caretColor: 'transparent'
            }}
            aria-hidden
          >
            {'\u00A0'}
          </Typography>
        </Box>
      )}
      {display === 'checking' && (
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}
        >
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Generating signal instructions…
          </Typography>
        </Box>
      )}
      {display === 'valid' && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            width: '100%'
          }}
        >
          <CheckCircleOutlineIcon
            sx={{
              fontSize: 18,
              color: 'success.main',
              flexShrink: 0,
              mt: 0.125
            }}
            aria-hidden
          />
          <Typography variant="body2" color="success.main" component="span">
            {`Found ${instructionCount} signal${instructionCount === 1 ? '' : 's'}.`}
          </Typography>
        </Box>
      )}
      {display === 'invalid' && invalidMessage && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            width: '100%'
          }}
        >
          <ErrorOutlineIcon
            sx={{
              fontSize: 18,
              color: 'error.main',
              flexShrink: 0,
              mt: 0.125
            }}
            aria-hidden
          />
          <Typography variant="body2" color="error" component="span">
            {invalidMessage}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default OnboardingSignalsValidator;
